import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  useTheme,
  useMediaQuery,
  Fade,
  Step,
  StepLabel,
  Stepper,
  StepConnector,
  styled
} from '@mui/material';
import {
  Edit,
  Palette,
  Download,
  ArrowForward,
  LightbulbOutlined
} from '@mui/icons-material';
import { useLanguage } from '../../../localization';
import tokens from '../../../theme/tokens';

// Styled components for the stepper
const CustomStepConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    borderColor: tokens.colors.neutral[200],
    borderTopWidth: 3,
  },
  // Hide connector lines on mobile
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const CustomStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: ownerState.active ? ownerState.color : tokens.colors.neutral[100],
  zIndex: 1,
  color: ownerState.active ? 'white' : tokens.colors.neutral[400],
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: tokens.shadows.sm,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: tokens.shadows.md,
    transform: 'scale(1.05)'
  },
  [theme.breakpoints.down('sm')]: {
    width: 40,
    height: 40,
  }
}));

const HowItWorks = ({ onGetStarted }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useLanguage();

  const steps = [
    {
      icon: <Edit />,
      title: t('howItWorks.steps.addInfo.title'),
      description: t('howItWorks.steps.addInfo.description'),
      color: tokens.colors.primary.main,
    },
    {
      icon: <Palette />,
      title: t('howItWorks.steps.chooseStyle.title'),
      description: t('howItWorks.steps.chooseStyle.description'),
      color: tokens.colors.secondary.main,
    },
    {
      icon: <Download />,
      title: t('howItWorks.steps.download.title'),
      description: t('howItWorks.steps.download.description'),
      color: tokens.colors.success,
    }
  ];

  const CustomStepIcon = (props) => {
    const { active, completed, icon, step } = props;
    
    return (
      <CustomStepIconRoot ownerState={{ active, color: steps[icon-1].color }}>
        {React.cloneElement(steps[icon-1].icon, {
          sx: { 
            fontSize: 24, 
            color: active ? 'white' : steps[icon-1].color 
          }
        })}
      </CustomStepIconRoot>
    );
  };

  return (
    <Box
      id="how-it-works"
      sx={{
        py: { xs: 4, md: 10 },
        background: `linear-gradient(135deg, ${tokens.colors.neutral[50]} 0%, ${tokens.colors.background.default} 100%)`,
        position: 'relative'
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.6rem', md: '2.5rem', lg: '3rem' },
              fontWeight: tokens.typography.fontWeights.bold,
              color: tokens.colors.text.primary,
              mb: { xs: 1, md: 2 }
            }}
          >
            {t('howItWorks.title')}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '0.9rem', md: '1.1rem' },
              color: tokens.colors.text.secondary,
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            {t('howItWorks.subtitle')}
          </Typography>
        </Box>

        {/* Mobile Stepper */}
        <Fade in timeout={500}>
          <Box sx={{ 
            width: '100%', 
            mb: { xs: 4, md: 6 },
            display: 'flex',
            justifyContent: 'center',
            [theme.breakpoints.down('sm')]: {
              px: 2
            }
          }}>
            <Stepper 
              alternativeLabel={!isSmallMobile} 
              orientation={isSmallMobile ? "vertical" : "horizontal"}
              activeStep={1} 
              connector={<CustomStepConnector />}
              sx={{
                [theme.breakpoints.down('sm')]: {
                  width: '100%',
                  maxWidth: '400px',
                  '& .MuiStep-root': {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    '& .MuiStepLabel-root': {
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      '& .MuiStepLabel-iconContainer': {
                        paddingRight: 0,
                        justifyContent: 'center',
                        width: '100%',
                        marginBottom: { xs: 0.5, md: 1 }
                      },
                      '& .MuiStepLabel-labelContainer': {
                        width: '100%',
                        textAlign: 'center'
                      }
                    }
                  }
                }
              }}
            >
              {steps.map((step, index) => (
                <Step key={step.title} active>
                  <StepLabel StepIconComponent={(props) => <CustomStepIcon {...props} step={step} icon={index+1} />}>
                    <Typography 
                      sx={{ 
                        fontWeight: tokens.typography.fontWeights.semibold,
                        color: tokens.colors.text.primary,
                        fontSize: { xs: '0.9rem', md: '1.1rem' },
                        mt: { xs: 0.5, md: 1 }
                      }}
                    >
                      {step.title}
                    </Typography>
                    {!isSmallMobile && (
                      <Typography 
                        sx={{ 
                          color: tokens.colors.text.secondary,
                          fontSize: { xs: '0.85rem', md: '0.9rem' },
                          display: { xs: 'none', sm: 'block' }
                        }}
                      >
                        {step.description}
                      </Typography>
                    )}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Fade>

        {/* Pro Tip Hint */}
        <Fade in timeout={800}>
          <Box
            sx={{
              textAlign: 'center',
              backgroundColor: `${tokens.colors.primary.main}05`,
              borderRadius: tokens.radius.xl,
              p: { xs: 3, md: tokens.spacing[6] },
              mt: { xs: 4, md: 6 },
              mb: { xs: 3, md: 4 },
              maxWidth: '600px',
              mx: 'auto'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                mb: 1
              }}
            >
              <LightbulbOutlined
                sx={{
                  color: tokens.colors.primary.main,
                  fontSize: { xs: '1.3rem', md: '1.5rem' }
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  color: tokens.colors.primary.main,
                  fontWeight: tokens.typography.fontWeights.semibold,
                  fontSize: { xs: '1rem', md: '1.1rem' }
                }}
              >
                {t('howItWorks.hint.title')}
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: tokens.colors.text.secondary,
                fontSize: { xs: '0.9rem', md: tokens.typography.fontSizes.base },
                lineHeight: tokens.typography.lineHeights.normal
              }}
            >
              {t('howItWorks.hint.description')}
            </Typography>
          </Box>
        </Fade>

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: { xs: 2, md: 4 } }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: tokens.typography.fontWeights.semibold,
              color: tokens.colors.text.primary,
              mb: 2,
              fontSize: { xs: '1.2rem', md: '1.5rem' }
            }}
          >
            {t('howItWorks.cta.title')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: tokens.colors.text.secondary,
              mb: 4,
              fontSize: { xs: '1rem', md: '1.1rem' },
              display: { xs: 'none', sm: 'block' }
            }}
          >
            {t('howItWorks.cta.subtitle')}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={onGetStarted}
            endIcon={<ArrowForward />}
            sx={{
              backgroundColor: tokens.colors.primary.main,
              color: 'white',
              fontWeight: tokens.typography.fontWeights.semibold,
              textTransform: 'none',
              px: { xs: 3, md: 4 },
              py: { xs: 1.2, md: 1.5 },
              fontSize: { xs: '1rem', md: '1.1rem' },
              borderRadius: tokens.radius.base,
              boxShadow: tokens.shadows.lg,
              '&:hover': {
                backgroundColor: tokens.colors.primary.dark,
                boxShadow: tokens.shadows.elegant,
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {t('howItWorks.cta.button')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default HowItWorks; 